import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import { routesAPI } from "../../services/api";
import type { Route } from "../../types/route";

interface FetchRoutesParams {
  page: number;
  sort: string;
  sport: string;
  sortOrder: number;
}

interface RoutesState {
  myRoutes: Route[];
  allRoutes: Route[];
  page: number;
  hasMore: boolean;
  status: "idle" | "loading" | "success" | "error";
  filters: {
    page: number;
    sort: string;
    sport: string;
    sortOrder: number;
  };
  currentRoute: Route | null;
}

const initialState: RoutesState = {
  myRoutes: [],
  allRoutes: [],
  page: 1,
  hasMore: true,
  status: "idle",
  filters: {
    page: 1,
    sort: "date",
    sport: "all",
    sortOrder: -1,
  },
  currentRoute: null,
};

// Async thunk to fetch routes with pagination
export const fetchRoutes = createAsyncThunk(
  "routes/fetchRoutes",
  async (params: FetchRoutesParams) => {
    const response = await routesAPI.getRoutes(params);
    return response.data;
  }
);

// Async thunk to fetch single route by ID
export const fetchRoute = createAsyncThunk("routes/fetchRoute", async (id: string) => {
  const response = await routesAPI.getRoute(id);
  return response.data;
});

// Async thunk to fetch all routes for global map
export const fetchAllRoutes = createAsyncThunk("routes/fetchAllRoutes", async () => {
  const response = await routesAPI.getAllRoutes();
  return response.data;
});

const routesSlice = createSlice({
  name: "routes",
  initialState,
  reducers: {
    resetRoutes: (state) => {
      state.myRoutes = [];
      state.page = 1;
      state.filters.page = 1;
      state.hasMore = true;
      state.status = "idle";
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.filters.sort = action.payload;
      state.filters.page = 1;
      state.page = 1;
      state.myRoutes = [];
      state.status = "idle";
    },
    setSportFilter: (state, action: PayloadAction<string>) => {
      state.filters.sport = action.payload;
      state.filters.page = 1;
      state.page = 1;
      state.myRoutes = [];
      state.status = "idle";
    },
    setSortOrder: (state, action: PayloadAction<number>) => {
      state.filters.sortOrder = action.payload;
      state.filters.page = 1;
      state.page = 1;
      state.myRoutes = [];
      state.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch routes
      .addCase(fetchRoutes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchRoutes.fulfilled, (state, action) => {
        state.status = "success";
        const routes = Array.isArray(action.payload) ? action.payload : [];

        // If page is 1, replace all routes, otherwise append
        if (state.filters.page === 1) {
          state.myRoutes = routes;
        } else {
          // Filter out duplicates by _id
          const existingIds = new Set(state.myRoutes.map((r) => r._id));
          const newRoutes = routes.filter((r) => !existingIds.has(r._id));
          state.myRoutes = [...state.myRoutes, ...newRoutes];
        }

        // Update page number and hasMore flag
        state.filters.page += 1;
        state.page = state.filters.page;
        state.hasMore = routes.length >= 20;
      })
      .addCase(fetchRoutes.rejected, (state) => {
        state.status = "error";
        state.hasMore = false;
      })
      // Fetch single route
      .addCase(fetchRoute.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchRoute.fulfilled, (state, action) => {
        state.status = "success";
        state.currentRoute = action.payload;
      })
      .addCase(fetchRoute.rejected, (state) => {
        state.status = "error";
      })
      // Fetch all routes
      .addCase(fetchAllRoutes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAllRoutes.fulfilled, (state, action) => {
        state.status = "success";
        state.allRoutes = action.payload;
      })
      .addCase(fetchAllRoutes.rejected, (state) => {
        state.status = "error";
      });
  },
});

export const { resetRoutes, setSortBy, setSportFilter, setSortOrder } = routesSlice.actions;
export default routesSlice.reducer;
